// START IMPORTS
// Core library imports
import { CookiesStorage } from '@ltvco/refresh-lib';
// Context and configuration imports
import {
  HiddenSectionsProvider,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Family and ancestry imports
import {
  FamilyTreeSection,
  useAncestryTree,
  familySearchAddressParser,
  useCreateFamilyTree,
  formatPostIndiFromOwner,
} from '@ltvco/refresh-lib/fmt';
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// Theme and UI imports
import { Box, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
  nameize,
  ageFromDob,
  formatAddressFromReport,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  BurialInformation,
  Connection,
  DebugMenu,
  ObituaryBanner,
  ObituaryDetails,
  Owner,
  PersonReport as PersonReportType,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  useRemouladeReportSnapshot,
  useReportMonitors,
  useSession,
  HideableSection,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  type ReportNavigationMenuProps,
  EmailSection as EmailSectionV2,
  PhoneSection as PhoneSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  NeighborsSection as NeighborsSectionV2,
  RelativesSection as RelativesSectionV2,
  AssociatesSection as AssociatesSectionV2,
  EducationSection as EducationSectionV2,
  JobsSection as JobsSectionV2,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  PersonReportOverview,
  ReportActionsWithDateUpdated,
  ReportRating as ReportRatingV2,
  NameAncestrySection,
  NotesSection as NotesSectionV2,
  MaritalRecordsSection as MaritalRecordsSectionV2,
  AssetsSection,
  CriminalOrTrafficSection as CriminalOrTrafficSectionV2,
  BankruptcySection as BankruptcySectionV2,
  CourtRecords,
  CourtRecordsLoadingScreen,
  RelatedReportsSection as RelatedReportsSectionV2,
  UsernameSection as UsernameSectionV2,
  LicensesAndPermitsSection as LicensesAndPermitsSectionV2,
  ReportClaimingSection,
  SocialSectionV2,
  NavLinkDataV2,
  NullableAdvancedSearchURLParams,
  LizanoListItemDataPerson,
  ReportNullState,
  RecordsFoundModal,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getPersonNavLinkDataV2 } from 'navLinkData/personNavLinkData';
import {
  formatFamilyTreeDataFromPersonReport,
  formatPostIndiFromConnection,
} from 'utils/formatFamilyTreeData';
import { getClaimedReports } from 'utils/getClaimedReports';

// Asset imports
import imgObituarySearch from '../../images/img_obituary-search.svg';
// Component imports
import { CourtSearchCTA } from '../../routes/Dashboard/CourtCases/components/CourtSearchCTA';

import { AncestorSearchBanner } from './SearchBanners/AncestorSearchBanner';
import { ReportBannerContainer } from './SearchBanners/components';
import { ObituarySearchBanner } from './SearchBanners/ObituarySearchBanner';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PersonReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'person';
// END CONSTANTS

export function PersonReport({ permalink }: PersonReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const currentUserInfo = account?.account?.user_info;
  const storage = new CookiesStorage();
  const navigate = useNavigate();
  const { isHidden } = useSubscriptionAccessConfigContext();

  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1569');
  const isInCourtCasesVariation = useFeatureIsOn('court-case-search');

  const { currentMonitors, reportIsMonitored } = useReportMonitors(permalink);
  const coreResult = useReport(permalink);
  const remouladeResult = useRemouladeReportSnapshot(permalink);

  const flattenedRemouladeResult = remouladeResult
    ? { ...remouladeResult, data: remouladeResult.data?.data }
    : remouladeResult;

  const shouldUseRemouladeSnapshot =
    !remouladeResult.isError && reportIsMonitored;
  const queryResult = (
    shouldUseRemouladeSnapshot ? flattenedRemouladeResult : coreResult
  ) as typeof coreResult;

  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();

  const isInSuggestedEmailVerification = localStorage.getItem(
    'emailVerificationSelected'
  );
  const customTimeDelay = isInSuggestedEmailVerification ? 500 : undefined;
  useScrollToSectionOnNavigate(hash, queryResult, customTimeDelay);

  const { data: familyTreeData } = useAncestryTree({
    enabledOnStart: true,
    logNotFoundErrorEnabled: false,
  });

  const { isCreatingFamilyTree, createFamilyTree } = useCreateFamilyTree();

  const queryClient = useQueryClient();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    userCode: currentUserInfo?.user_code,
    potentialOwnerIndex: 0,
    hideContactInfo: false,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });

  const [showCriminalData, setShowCriminalData] = useState(false);
  const [isCourtRecordsLoading, setIsCourtRecordsLoading] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [disableClaiming, setDisableClaiming] = useState(false);
  const [showNewRecordsModal, setShowNewRecordsModal] = useState(false);
  const [claimedReportPermalink, setClaimedReportPermalink] =
    useState<string>('');
  const [updateDate, setUpdateDate] = useState<string | undefined>();
  const [report, setReport] = useState<PersonReportType | null>(null);
  const [reportBvId, setReportBvId] = useState<string>('');
  const [filteredRelatives, setFilteredRelatives] = useState<Connection[]>([]);
  const [reportOwner, setReportOwner] = useState<Owner | null>(null);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const date = new DateUtil();

  const alternativeTitle = isInPartialAddressesVariation
    ? 'Possible Locations'
    : undefined;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    const isAnotherReportClaimed = currentMonitors?.some(
      (monitor: ReportMonitor) => {
        return (
          monitor.report_type === 'detailed_person_report' &&
          monitor.is_claimed &&
          permalink !== monitor.permalink
        );
      }
    );
    setDisableClaiming(isAnotherReportClaimed);
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    setReportOptions((prevOptions: ReportOptions) => ({
      ...prevOptions,
      userCode: currentUserInfo?.user_code,
    }));
  }, []);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      // The format of queryResult from REMO has an extra data key.
      if (queryResult?.data?.entities)
        setReport(ReportFactory.create(queryResult?.data, 'person'));
    }
    if (!isInPartialAddressesVariation) {
      storage.setValue('partialLocView', 'false');
    }
  }, [queryResult.data, queryResult.isSuccess]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate)
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    else if (coreUpdateDate)
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);

  useEffect(() => {
    if (!currentMonitors) return;

    const REPORT_TYPE = 'detailed_person_report';
    const claimedReports = getClaimedReports(currentMonitors, REPORT_TYPE);

    if (claimedReports.length) {
      const mainPermalink = claimedReports[0].permalink;
      setClaimedReportPermalink(mainPermalink);
    } else {
      if (claimedReportPermalink !== '') {
        setClaimedReportPermalink('');
      }
    }
  }, [currentMonitors, claimedReportPermalink]);

  useEffect(() => {
    if (report) {
      const tempReportOwner = report.getOwner(reportOptions);
      if (tempReportOwner) {
        setReportOwner(tempReportOwner);
        setFilteredRelatives(tempReportOwner.relatives || []);
      }

      const queryParameters =
        report?.data?.meta?.request_info?.query_parameters;

      if (queryParameters) {
        const bvidObj = queryParameters.find(
          (param: { name: string }) => param.name === 'bvid'
        );
        setReportBvId(bvidObj?.['values']?.[0] || '');
      }
    }
  }, [report, reportOptions]);

  useEffect(() => {
    if (!!familyTreeData?.indis && filteredRelatives.length > 0) {
      const relativesFromFamilyTree = familyTreeData.indis;
      const confirmedRelativesBvid = relativesFromFamilyTree.map(
        (relative) => relative.bvid
      );

      const tempFilteredRelatives = filteredRelatives.filter((relative) => {
        return !confirmedRelativesBvid.includes(relative.bvId);
      });

      // Prevents infinite setting of filtered relatives.
      if (tempFilteredRelatives.length !== filteredRelatives.length) {
        setFilteredRelatives(tempFilteredRelatives);
      }
    }
  }, [familyTreeData, filteredRelatives]);
  // END EFFECTS

  // START COMPUTED_VALUES
  const hideCriminalBJLData = account?.account.user_settings
    .hide_criminal_records as boolean;

  const licensesCount = useMemo(() => {
    const licenses = reportOwner?.bonusData?.licenses;
    const filings = reportOwner?.bonusData?.filings;

    return (
      (licenses?.dea?.length ?? 0) +
      (licenses?.faa?.length ?? 0) +
      (licenses?.professional?.length ?? 0) +
      (licenses?.sporting?.length ?? 0) +
      (licenses?.weapon?.length ?? 0) +
      (filings?.business?.length ?? 0) +
      (filings?.ucc?.length ?? 0)
    );
  }, [reportOwner?.bonusData]);

  const recordsFound = useMemo(() => {
    const data = [
      {
        label: 'Criminal or Traffic records',
        count: reportOwner?.courts?.criminal?.length ?? 0,
      },
      {
        label: 'Bankruptcies',
        count: reportOwner?.courts?.bankruptcy?.length ?? 0,
      },
      { label: 'Licenses & Permits', count: licensesCount || 0 },
    ];
    if (data.every(({ count }) => count === 0)) return [];
    return data.map(({ label, count }) => `${count} ${label}`);
  }, [licensesCount, reportOwner?.courts]);
  // END COMPUTED_VALUES

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={14} />;
  }

  if (zeroed) {
    return <ReportNullState />;
  }

  if (!reportOwner || !report) return <ReportLoading menuItems={14} />;

  if (!report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const reportUpgraded = queryResult?.data?.meta?.report_upgraded;
  const revealFreeBonusData = showCriminalData && !isCourtRecordsLoading;

  let showReportClaiming = false;
  if (
    currentUserInfo?.first_name &&
    currentUserInfo.last_name &&
    report?.personName.includes(currentUserInfo.first_name) &&
    report?.personName.includes(currentUserInfo.last_name) &&
    !disableClaiming
  ) {
    showReportClaiming = true;
  }

  const MonitoringComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const {
    ownerName,
    nameInfo,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    marital,
    relatives,
    associates,
    neighbors,
    courts,
    obituary,
    grave,
    bonusData,
    location,
    identity,
  } = reportOwner as Owner;

  const familyTreeRelatives = familyTreeData?.indis || [];

  const personNavLinkDataV2 = getPersonNavLinkDataV2(
    reportOwner as Owner,
    hideCriminalBJLData,
    revealFreeBonusData,
    relatives || [],
    familyTreeRelatives
  );

  const dob =
    reportOwner.identity?.dobs?.[0]?.date?.parsed?.year?.toString() ?? '';
  const dod = reportOwner.identity?.dods[0]?.date?.full || '';
  const isDeceased = !!dod;
  const shouldShowObituarySection = !!obituary?.funeralHome;
  const shouldShowObituaryCta = isDeceased && !shouldShowObituarySection;
  document.title = `${report.personName} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataPerson = {
      permalink,
      first_name: nameize(nameInfo?.parsed?.first ?? ''),
      last_name: nameize(nameInfo?.parsed?.last ?? ''),
      middle_name: nameize(nameInfo?.parsed?.middle ?? ''),
      person_id: reportBvId || '',
      age: String(ageFromDob(dob)) || '',
      addresses: addresses.map((address) => ({
        full_adddress: address?.full || '',
        street: address.parsed
          ? formatAddressFromReport(address.parsed).street
          : '',
        city: address.parsed?.city || '',
        state: address.parsed?.state || '',
        zip5: address.parsed?.zip5 || '',
      })),
      phones: phones.map((phone) => ({
        number: phone.number || '',
      })),
      emails: emails.map((email) => ({
        address: email.address || '',
      })),
    };

    return data;
  };

  const reportInFamilyTree: boolean | undefined = familyTreeData?.indis
    ? !!familyTreeData.indis.some((relative) => {
        if (relative.placeholder) return false;
        return relative.reports?.some(
          (report) => 'bvid' in report && report.bvid === reportBvId
        );
      })
    : undefined;

  const memberIdInFamilyTree = reportInFamilyTree
    ? familyTreeData?.indis?.find((relative) => {
        return 'bvid' in relative && relative.bvid === reportBvId;
      })?.id
    : undefined;

  const nameizedName = nameize(ownerName);

  const locationParser = () => {
    const isUS = (country?: string | null): boolean => {
      if (!country) return false;
      const usVariations = [
        'United States',
        'USA',
        'United States of America',
        'America',
        'US',
        'U.S.',
        'U.S.A.',
      ];
      return usVariations.includes(country);
    };

    // Prefer parsed location if available
    // Handle both state (US) and province (international) fields
    const stateOrProvince =
      location?.parsed?.state || location?.parsed?.province;
    if (stateOrProvince || isUS(location?.parsed?.country)) {
      const parsedLocation = [location?.parsed?.city, stateOrProvince]
        .filter(Boolean)
        .join(', ');

      const parsedLocationData = {
        city: location?.parsed?.city || '',
        country: isUS(location?.parsed?.country)
          ? 'United States'
          : location?.parsed?.country || 'United States',
        state: stateOrProvince || '',
        location: parsedLocation,
      };
      return parsedLocationData;
    } else if (location?.parsed?.country) {
      const parsedLocation = [location?.parsed?.city, location?.parsed?.country]
        .filter(Boolean)
        .join(', ');

      const parsedLocationData = {
        city: location?.parsed?.city || '',
        country: location?.parsed?.country || '',
        location: parsedLocation,
      };
      return parsedLocationData;
    } else if (location?.full) {
      // Parse the full location string using familySearchAddressParser
      const parsed = familySearchAddressParser(location.full);
      const parsedLocation = [parsed.city, parsed.state, parsed.country]
        .filter(Boolean)
        .join(', ');
      return {
        city: parsed.city || '',
        country: parsed.country || '',
        state: parsed.state || '',
        location: parsedLocation,
      };
    }
    // Return default values if no location data is available
    return {
      city: '',
      country: '',
      location: '',
    };
  };

  const parsedLocationData = locationParser();

  const personSearchParams: NullableAdvancedSearchURLParams = {
    ...parsedLocationData,
    fullName: nameizedName || '',
    fName: nameize(nameInfo?.parsed?.first || ''),
    lName: nameize(nameInfo?.parsed?.last || ''),
    isDeceased: true,
    birthMonth:
      identity?.dobs?.[0]?.date?.parsed?.month?.toString().padStart(2, '0') ||
      null,
    birthYear: dob || null,
    deathYear: identity?.dods?.[0]?.date?.parsed?.year?.toString() || null,
    deathMonth:
      identity?.dods?.[0]?.date?.parsed?.month?.toString().padStart(2, '0') ||
      null,
  };

  const birthYear = dob ? parseInt(dob, 10) : null;
  const isPersonBornBeforeYear = birthYear && birthYear < 1900;
  const redirectUrl = `${window.location}`;
  // END DATA_PREPARATION

  // START EVENT_HANDLERS
  const handleAddingRelativeToFamilyTree = async () => {
    // Find the person report's data to format.
    const person = report.data.people?.[0];
    if (!person) return null;

    if (reportInFamilyTree) {
      localStorage.setItem(
        'familyTreeMemberId',
        JSON.stringify(memberIdInFamilyTree)
      );
      navigate('/family-tree');
      return;
    }

    if (!familyTreeData) {
      // If the user doesn't have a family tree, we need to post the person to the family tree.
      // So the tree is created with the person as main member.
      const reportIndi = formatPostIndiFromOwner(reportOwner as Owner);
      if (!reportIndi) return;

      // Using await prevents the user from navigating to the family tree before the tree is created.
      await createFamilyTree({
        indi: reportIndi,
        permalink,
        bvid: reportBvId,
      });

      return;
    }

    localStorage.setItem(
      'familyTreeRelativeToAdd',
      JSON.stringify(
        formatFamilyTreeDataFromPersonReport(
          person,
          permalink,
          person.identity.ids[0] || '',
          addresses[0]?.parsed || {}
        )
      )
    );

    navigate(`/family-tree?from=${permalink}`);
  };

  const handleAddingPossibleRelativeToFamilyTree = async (bvid: string) => {
    // Find the person report's relative's data to format.
    const relative = relatives?.find(
      (person) => person.identity.ids[0] === bvid
    );
    if (!relative) return null;

    if (!familyTreeData) {
      // If the user doesn't have a family tree, we need to post the person to the family tree.
      // So the tree is created with the person as main member.
      const reportIndi = formatPostIndiFromConnection(
        relative,
        relative.addresses[0]?.parsed || {}
      );
      if (!reportIndi) return;

      await createFamilyTree({
        indi: reportIndi,
        permalink: undefined,
        bvid: bvid,
        isPossibleRelativeFromPersonReport: true,
      });

      return;
    }

    localStorage.setItem(
      'familyTreeRelativeToAdd',
      JSON.stringify(
        formatFamilyTreeDataFromPersonReport(
          relative,
          null,
          bvid,
          relative.addresses[0]?.parsed || {}
        )
      )
    );

    navigate(`/family-tree?from=${permalink}`);
  };

  const delayForCourtRecords = 1000;

  function handleCourtRecordsLoadingScreen() {
    setIsCourtRecordsLoading(true);
    setTimeout(() => {
      setIsCourtRecordsLoading(false);
      setShowNewRecordsModal(true);
      setShowCriminalData(true);
    }, delayForCourtRecords * 7);
  }

  // END EVENT_HANDLERS

  // START RENDER
  return (
    <HiddenSectionsProvider reportType="detailed_person_report">
      <Box>
        <Stack direction="row">
          {/* START DESKTOP_NAVIGATION */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
            }}
          >
            <ReportNavigationMenu
              reportType="Person Report"
              headerTitle={ownerName}
              menuItems={
                personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
              }
            />
          </Box>
          {/* END DESKTOP_NAVIGATION */}

          <Box marginTop={0}>
            <Box
              sx={{
                height: 30,
                backgroundColor: '#f8f8f8',
                position: 'sticky',
                marginBottom: '-10px',
                top: 52,
                zIndex: 10,
                display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
              }}
            />

            <ReportActionsWithDateUpdated
              reportType={reportType}
              reportTitle="Person"
              dateUpdated={updateDate}
              permalink={permalink}
              navLinkData={personNavLinkDataV2 as unknown as NavLinkDataV2[]}
              reportUpgraded={reportUpgraded}
              reportDataForListV2={
                isInListV2Variation ? buildReportDataForListV2() : undefined
              }
              isConfirmed={showCriminalData}
              revealFreeBonusData={revealFreeBonusData}
            />

            <PersonReportOverview
              isConfirmed={showCriminalData}
              reportType={reportType}
              owner={reportOwner as Owner}
              personName={ownerName}
              personOverviewItemsData={{
                hideCriminalBJLData,
                phones,
                emails,
                addresses,
                relatives,
                images: reportOwner!.images,
                profiles,
                usernames,
                jobs,
                educations,
              }}
              dateUpdated={updateDate}
              permalink={permalink}
              navLinkData={personNavLinkDataV2 as unknown as NavLinkDataV2[]}
              reportUpgraded={reportUpgraded}
              familyTreeCTA={true}
              reportInFamilyTree={reportInFamilyTree}
              addRelativeToFamilyTree={handleAddingRelativeToFamilyTree}
              isCreatingFamilyTree={isCreatingFamilyTree}
              reportDataForListV2={
                isInListV2Variation ? buildReportDataForListV2() : undefined
              }
            />

            {shouldShowObituaryCta && (
              <ObituaryBanner image={imgObituarySearch} />
            )}

            {MonitoringComponent}

            <HideableSection
              nameToDisplay="Possible Phone Numbers"
              nameToRecord="possible_phone_numbers"
              id="phone-numbers-section"
            >
              <PhoneSectionV2
                personName={ownerName}
                phoneList={phones}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Email Addresses"
              nameToRecord="possible_email_addresses"
              id="email-section"
            >
              <EmailSectionV2
                personName={ownerName}
                emailList={emails}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Address History"
              nameToRecord="possible_address_history"
              id="address-history-section"
            >
              <AddressHistorySectionV2
                addresses={addresses}
                personName={ownerName}
                permalink={permalink}
                alternateTitle={alternativeTitle}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Social Media"
              nameToRecord="possible_social_media"
              id="possible-social-media"
            >
              <SocialSectionV2
                personName={ownerName}
                profiles={profiles}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Usernames"
              nameToRecord="usernames-section"
              id="possible-usernames"
            >
              <UsernameSectionV2 personName={ownerName} usernames={usernames} />
            </HideableSection>

            <HideableSection
              nameToDisplay="Names & Ancestry"
              nameToRecord="names_and_ancestry"
              id="names-ancestry-section"
            >
              <NameAncestrySection
                firstName={nameize(nameInfo?.parsed?.first ?? null)}
                surName={nameize(nameInfo?.parsed?.last ?? null)}
                yearOfBirth={dob}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Family Tree"
              nameToRecord="family_tree"
              id="family-tree-section"
            >
              <FamilyTreeSection
                permalink={permalink}
                familyTreeData={familyTreeData}
                personName={ownerName}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Relatives"
              nameToRecord="possible_relatives"
              id="relatives-section"
            >
              <RelativesSectionV2
                personName={ownerName}
                relatives={relatives || []}
                permalink={permalink}
                familyTreeData={familyTreeData}
                addRelativeToFamilyTree={
                  handleAddingPossibleRelativeToFamilyTree
                }
              />
            </HideableSection>

            <>
              {shouldShowObituarySection && (
                <ObituaryDetails obituary={obituary} permalink={permalink} />
              )}
              {grave && (
                <BurialInformation grave={grave} permalink={permalink} />
              )}
            </>

            <HideableSection
              nameToDisplay="Possible Marriage & Divorce Records"
              nameToRecord="possible_marriage_divorce_records"
              id="marital-records-section"
            >
              <MaritalRecordsSectionV2
                personName={ownerName}
                marital={marital}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Associates"
              nameToRecord="possible_associates"
              id="associates-section"
            >
              <AssociatesSectionV2
                personName={ownerName}
                associates={associates || []}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Neighbors"
              nameToRecord="possible_neighbors"
              id="neighbors-section"
            >
              <NeighborsSectionV2
                personName={ownerName}
                neighbors={neighbors || []}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Court Orders"
              nameToRecord="court_orders"
              id="criminal-or-traffic-section"
            >
              {!hideCriminalBJLData && (
                <>
                  {revealFreeBonusData ? (
                    <>
                      <CriminalOrTrafficSectionV2
                        criminalOrTrafficData={courts.criminal || []}
                        personName={ownerName}
                      />
                      {isInCourtCasesVariation && (
                        <CourtSearchCTA personName={ownerName} />
                      )}
                      <BankruptcySectionV2
                        personName={ownerName}
                        bankruptcyData={courts.bankruptcy || []}
                      />
                      <LicensesAndPermitsSectionV2
                        personName={ownerName}
                        bonusData={bonusData}
                      />
                    </>
                  ) : (
                    <>
                      {isCourtRecordsLoading ? (
                        <CourtRecordsLoadingScreen
                          personName={ownerName}
                          delayDuration={delayForCourtRecords}
                        />
                      ) : (
                        <CourtRecords
                          handleCourtRecordsLoadingScreen={
                            handleCourtRecordsLoadingScreen
                          }
                          redirectUrl={redirectUrl}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {!revealFreeBonusData && (
                <CourtSearchCTA personName={ownerName} />
              )}

              <RecordsFoundModal
                open={showNewRecordsModal}
                handleClose={() => setShowNewRecordsModal(false)}
                recordsFound={recordsFound}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Jobs"
              nameToRecord="possible_jobs"
              id="jobs-section"
            >
              <JobsSectionV2
                personName={ownerName}
                jobs={jobs}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Education"
              nameToRecord="possible_education"
              id="education-section"
            >
              <EducationSectionV2
                personName={ownerName}
                educations={educations}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="Possible Owned Assets"
              nameToRecord="possible_owned_assets"
              id="possible-owned-assets-section"
            >
              <AssetsSection
                personName={ownerName}
                ownedAssets={ownedAssets}
                permalink={permalink}
              />
            </HideableSection>

            <HideableSection
              nameToDisplay="notes"
              nameToRecord="notes"
              id="Notes-section"
            >
              <NotesSectionV2 permalink={permalink} />
            </HideableSection>

            {(isDeceased || isPersonBornBeforeYear) && (
              <ReportBannerContainer>
                {!isHidden('obituary') && (
                  <ObituarySearchBanner searchParams={personSearchParams} />
                )}
                {!isHidden('ancestry') && (
                  <AncestorSearchBanner searchParams={personSearchParams} />
                )}
              </ReportBannerContainer>
            )}

            <HideableSection
              nameToDisplay="Related Reports"
              nameToRecord="related_reports"
              id="related-reports-section"
            >
              <RelatedReportsSectionV2
                reportType="person"
                personName={ownerName}
                potentialOwners={report.data.people}
                relatives={relatives}
                addresses={addresses}
                phones={phones}
                emails={emails}
                usernames={usernames}
              />
            </HideableSection>

            <ReportRatingV2
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Box>
        </Stack>

        {/* START MOBILE_NAVIGATION */}
        <ReportNavigationMobileMenu
          reportType="Person Report"
          headerTitle={ownerName}
          menuItems={
            personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
          }
        />
        {/* END MOBILE_NAVIGATION */}

        {/* START DEBUG_MENU */}
        <DebugMenu menuItems={report.data.rawData.debug_menu} />
        {/* END DEBUG_MENU */}
      </Box>
    </HiddenSectionsProvider>
  );
  // END RENDER
}
